import { Popover, Transition } from "@headlessui/react";
import Slider from "rc-slider";
import { Fragment, MouseEvent, useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import ButtonPrimary from "@/shared/Button/ButtonPrimary";
import ButtonThird from "@/shared/Button/ButtonThird";
import Checkbox from "@/shared/Checkbox/Checkbox";
import convertNumbThousand from "@/utils/convertNumbThousand";
import { useSearchParams } from "react-router-dom";
import { XClear } from "@/shared/XClear"

const transitSubcategory = {
  AIRPORT: 'Airport',
  RAILWAY_STATION: 'Railway Station',
  BUS_TERMINAL: 'Bus Terminal',
  METRO_STATION: 'Metro Station'
};

const retailSubcategory = {
  SHOPPING_MALL: 'Shopping Mall',
  SUPERMARKET: 'Supermarket',
  CONVENIENCE_STORE: 'Convenience Store',
  SHOWROOM: 'Showroom'
};

const outdoorSubcategory = {
  STREET_FURNITURE: 'Street Furniture',
  DIGITAL_KIOSK: 'Digital Kiosk',
  LED_HOARDING: 'LED Hoarding'
};

const healthcareSubcategory = {
  HOSPITAL: 'Hospital',
  PHARMACY: 'Pharmacy',
  GYM: 'Gym',
  SALON: 'Salon',
  CLINIC: 'Clinic',
  DIAGNOSTIC_CENTER: 'Diagnostic Center',
  WELLNESS_CENTER: 'Wellness Center'
};

const educationSubcategory = {
  UNIVERSITY: 'University',
  SCHOOL: 'School',
  LIBRARY: 'Library'
};

const buildingSpacesSubcategory = {
  OFFICE_LOBBY: 'Office Lobby',
  CONFERENCE_ROOM: 'Conference Room',
  COMMON_AREA: 'Common Area',
  LIFT_LOBBY_OFFICE: 'Lift Lobby (Office)',
  RESIDENTIAL_LOBBY: 'Residential Lobby',
  RESIDENTIAL_LIFT_LOBBY: 'Residential Lift Lobby',
  CLUBHOUSE: 'Clubhouse',
  PARKING_AREA: 'Parking Area',
  CITY_HALL: 'City Hall',
  PUBLIC_SERVICE_OFFICE: 'Public Service Office',
  CIVIC_CENTER: 'Civic Center'
};

const leisureSubcategory = {
  CINEMA: 'Cinema',
  SPORTS_ARENA: 'Sports Arena',
  CLUB: 'Club'
};

const financialSubcategory = {
  BANK: 'Bank',
  ATM: 'ATM',
  INSURANCE_OFFICE: 'Insurance Office'
};

export type TabFiltersType = {
  adBoardTypes: Record<string, boolean>,
  locationTypes: Record<string, boolean>,
  subcategories: Record<string, boolean>,
  rangePrices: number[]
}

type TabFilterProps = {
  filters?: TabFiltersType,
  onChange?: ({
    adBoardTypes,
    rangePrices
  }: TabFiltersType) => void
}

type SelectedTabs = Record<string, boolean>

const categoryOptions = {
  transitSubcategory:{
    name:"Transit",
    options: Object.keys(transitSubcategory).map(key => ({ id: key, name: transitSubcategory[key] }))
  },
  retailSubcategory:{
    name:"Retail",
    options: Object.keys(retailSubcategory).map(key => ({ id: key, name: retailSubcategory[key] }))
  },
  outdoorSubcategory:{
    name:"Outdoor",
    options: Object.keys(outdoorSubcategory).map(key => ({ id: key, name: outdoorSubcategory[key] }))
  },
  healthcareSubcategory:{
    name:"Healthcare",
    options: Object.keys(healthcareSubcategory).map(key => ({ id: key, name: healthcareSubcategory[key] }))
  },
  educationSubcategory:{
    name:"Education",
    options: Object.keys(educationSubcategory).map(key => ({ id: key, name: educationSubcategory[key] }))
  },
  buildingSpacesSubcategory:{
    name:"Building Spaces",
    options: Object.keys(buildingSpacesSubcategory).map(key => ({ id: key, name: buildingSpacesSubcategory[key] }))
  },
  leisureSubcategory:{
    name:"Leisure",
    options: Object.keys(leisureSubcategory).map(key => ({ id: key, name: leisureSubcategory[key] }))
  },
  financialSubcategory:{
    name:"Financial",
    options: Object.keys(financialSubcategory).map(key => ({ id: key, name: financialSubcategory[key] }))
  }
};

const TabsTypeOfPlace = ({ 
  options,label,selectedTabs:defaultSelectedTabs,onChange
}: {
   options:{id:string,name:string}[],
   label?:string,
   onChange: (arg: SelectedTabs) => void, 
   selectedTabs: SelectedTabs }) => {

  const [selectedTabs, setSelectedTabs] = useState<SelectedTabs>(defaultSelectedTabs);

  const optionMap = options.reduce<Record<string, any>>((a, b) => {
    a[b.id] = b
    return a;
  }, {})||{}

  const selected = Object.entries(defaultSelectedTabs).filter(item => item[1]).map(item => optionMap[item[0]]?.name)

  function handleClick(e: MouseEvent) {
    e.stopPropagation();
    setSelectedTabs({});
    onChange({})
  }

  return (
    <Popover className="relative">
      {({ open, close: closeDialog }) => (
        <>
          <Popover.Button
            className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border ${selected.length ? "border-primary-500 bg-primary-50 text-primary-700 focus:outline-none " : "border-neutral-300 dark:border-neutral-700 focus:outline-none"} ${open ? "!border-primary-500 " : ""
            }`}
          >
            {selected.length ? <>
              <span>{selected.join(", ")}</span>
              <XClear onClick={handleClick} />
            </> : <>
              <span>{label||"Adboard type"}</span>
              <i className="las la-angle-down ml-2"></i>
            </>}
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-10 w-screen max-w-sm px-4 mt-3 left-0 sm:px-0 lg:max-w-md">
              <div className="overflow-hidden rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
                <div className="relative flex flex-col px-5 py-6 space-y-5">
                  {options?.map((item) => (
                    <div key={item.name} className="">
                      <Checkbox
                        defaultChecked={!!selectedTabs[item.id]}
                        onChange={(checked) => {
                          setSelectedTabs({
                            ...selectedTabs,
                            [item.id]: checked
                          })
                        }}
                        name={item.name}
                        label={item.name}
                      />
                    </div>
                  ))}
                </div>
                <div className="p-5 bg-neutral-50 dark:bg-neutral-900 dark:border-t dark:border-neutral-800 flex items-center justify-between">
                  <ButtonThird onClick={closeDialog} sizeClass="px-4 py-2 sm:px-5">
                    Clear
                  </ButtonThird>
                  <ButtonPrimary
                    onClick={() => {
                      closeDialog();
                      onChange?.(selectedTabs)
                    }}
                    sizeClass="px-4 py-2 sm:px-5"
                  >
                    Apply
                  </ButtonPrimary>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

const TabFilters = ({
}: TabFilterProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState<number[]>([0, 100000]);

  const rangePrices = JSON.parse(searchParams.get('rangePrices') as string) || [0, 0]

  const selectedSubcategoriesMap = Object.fromEntries(
    Object.keys(categoryOptions).map(category => [
      category,
      searchParams.get(category)?.split(',').reduce((a, b) => {
        if(!b) return a;
        a[b] = true
        return a
      }, {})||{}
    ])
  );
  
  function handleClearClick(type:string){
    if(type === "price"){
      setSearchParams({
        ...searchParams,
        rangePrices:JSON.stringify([0,100000])
      })
    }
    close()
  }
  const renderTabsPriceRage = () => {

    return (
      <Popover className="relative">
        {({  close }) => (
          <>
            <Popover.Button
              className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border    focus:outline-none ${(rangePrices[0]||rangePrices[1]) ? 'border-primary-500 text-primary-700 bg-primary-50':"border-neutral-300 dark:border-neutral-700 focus:outline-none"}`}
            >
              {rangePrices[0]||rangePrices[1] ?
                <>
                  <span>
                    {`₹${convertNumbThousand(
                      rangePrices[0]
                    )} - ₹${convertNumbThousand(rangePrices[1])}`}{" "}
                  </span>
                  <XClear onClick={(e)=>{e.stopPropagation() ; setSearchParams({rangePrices:JSON.stringify([0,0])})}}/>
                </>:
                <>
                  <span>Price range</span>
                </>}
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-screen max-w-sm px-4 mt-3 left-0 sm:px-0 ">
                <div className="overflow-hidden rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
                  <div className="relative flex flex-col px-5 py-6 space-y-8">
                    <div className="space-y-5">
                      <span className="font-medium">Price per day</span>
                      <Slider
                        range
                        className="text-red-400"
                        min={0}
                        max={20000}
                        value={range}
                        allowCross={false}
                        onChange={(e) => setRange(e as number[])}
                      />
                    </div>

                    <div className="flex justify-between space-x-5">
                      <div>
                        <label
                          htmlFor="minPrice"
                          className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                        >
                          Min price
                        </label>
                        <div className="mt-1 relative rounded-md">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-neutral-500 sm:text-sm">
                            ₹
                            </span>
                          </div>
                          <input
                            type="text"
                            name="minPrice"
                            id="minPrice"
                            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-3 sm:text-sm border-neutral-200 rounded-full text-neutral-900"
                            value={range[0]}
                            onChange={(e) => setRange([Number(e.target.value),range[1]])}
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="maxPrice"
                          className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                        >
                          Max price
                        </label>
                        <div className="mt-1 relative rounded-md">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-neutral-500 sm:text-sm">
                            ₹
                            </span>
                          </div>
                          <input
                            type="text"
                            name="maxPrice"
                            id="maxPrice"
                            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-3 sm:text-sm border-neutral-200 rounded-full text-neutral-900"
                            value={range[1]}
                            onChange={(e) => setRange([range[0],Number(e.target.value)])}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 bg-neutral-50 dark:bg-neutral-900 dark:border-t dark:border-neutral-800 flex items-center justify-between">
                    <ButtonThird onClick={()=>{close();handleClearClick('price')}} sizeClass="px-4 py-2 sm:px-5">
                      Clear
                    </ButtonThird>
                    <ButtonPrimary
                      onClick={() => {
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.set('rangePrices', JSON.stringify(range));
                        setSearchParams(newSearchParams)
                        close();
                      }}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const renderFilters = () => (
    <div className="flex flex-wrap gap-2 items-center">
      Filters: 
      {renderTabsPriceRage()}
      {Object.entries(categoryOptions).map(([category, {name,options}]) => {
        const paramKey = category;
        return (
          <TabsTypeOfPlace
            key={category}
            label={name}
            selectedTabs={selectedSubcategoriesMap[category]}
            onChange={(selectedTabs: any) => {
              const newSearchParams = new URLSearchParams(searchParams);
              const selectedValues = Object.entries(selectedTabs)
                .filter(item => item[1])
                .map(item => item[0])
                .join(',');
              
              if (selectedValues) {
                newSearchParams.set(paramKey, selectedValues);
              } else {
                newSearchParams.delete(paramKey);
              }
              
              setSearchParams(newSearchParams);
            }}
            options={options}
          />
        );
      })}
      
    </div>
  );

  return (
    <div className="flex lg:space-x-4">
      {isMobile ? (
        <>
          <button
            onClick={() => setIsMobileFilterOpen(true)}
            className="flex items-center justify-center px-4 py-2 text-sm rounded-full border border-neutral-300 dark:border-neutral-700 focus:outline-none"
          >
            <span>Filters</span>
            <i className="las la-sliders-h ml-2"></i>
          </button>

          <Dialog
            open={isMobileFilterOpen}
            onClose={() => setIsMobileFilterOpen(false)}
            className="relative z-50"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
                  Filters
                </Dialog.Title>
                <div className="flex flex-col gap-4">
                  {renderFilters()}
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-primary-100 px-4 py-2 text-sm font-medium text-primary-900 hover:bg-primary-200 focus:outline-none"
                    onClick={() => setIsMobileFilterOpen(false)}
                  >
                    Done
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </Dialog>
        </>
      ) : (
        <div className="flex lg:flex-row flex-col gap-4">
          {renderFilters()}
        </div>
      )}
    </div>
  );
};

export default TabFilters;
